import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Download, FileText, FileSpreadsheet, TrendingUp, DollarSign, Send, BarChart3, X, AlertTriangle, AlertCircle, CheckCircle2, Calculator as CalcIcon, ChevronRight, ChevronDown } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';
import { SearchableSelectLazy } from '@/components/SearchableSelectLazy';
import ServiceManagerEnhanced from '@/components/ServiceManagerEnhanced';
import { US_STATES } from '@/data/usLocations';
import { validateCalculatorInputs, calculateVolumeTotal as calcVolTotal, calculateDistributionTotal as calcDistTotal } from '@/components/calculator/ValidationHelpers';
import ClientInfoBlock from '@/components/calculator/ClientInfoBlock';
import TargetMarginsDisplay from '@/components/calculator/TargetMarginsDisplay';
import VolumeAssumptionsBlock from '@/components/calculator/VolumeAssumptionsBlock';
import IncumbentPricingBlock from '@/components/calculator/IncumbentPricingBlock';
import ProposedPricingBlock from '@/components/calculator/ProposedPricingBlock';
import DistributionBlock from '@/components/calculator/DistributionBlock';
import ReaderCostRecoveryTool from '@/components/calculator/ReaderCostRecoveryTool';
import GWProfitabilityResults from '@/components/calculator/GWProfitabilityResults';
import ProfitabilityTablesDetailed from '@/components/ProfitabilityTablesDetailed';
import CalculatorProgressTracker from '@/components/calculator/CalculatorProgressTracker';
import CalculatorHelp from '@/components/calculator/CalculatorHelp';
import QuickStartTemplates from '@/components/calculator/QuickStartTemplates';
import ApprovalFlags from '@/components/calculator/ApprovalFlags';
import ProposalSelector from '@/components/ProposalSelector';
import MarginGuideSidebar from '@/components/calculator/MarginGuideSidebar';
import CalculatorHeader from '@/components/calculator/CalculatorHeader';
import CalculatorActions from '@/components/calculator/CalculatorActions';
import ProposalSummary from '@/components/calculator/ProposalSummary';
import ProposalScenariosList from '@/components/calculator/ProposalScenariosList';
import PdfViewerModal from '@/components/PdfViewerModal';
import ProfitabilityTables from '@/components/ProfitabilityTables';
import { useCalculatorData } from '@/hooks/useCalculatorData';
import { useProposalManagement } from '@/hooks/useProposalManagement';
import { useScenarioManagement } from '@/hooks/useScenarioManagement';
import { usePricingCalculations } from '@/hooks/usePricingCalculations';
import CalculatorValidation from '@/components/calculator/CalculatorValidation';
import SmartSuggestions from '@/components/calculator/SmartSuggestions';
import { useAutoSave, AutoSaveIndicator } from '@/hooks/useAutoSave';



export default function Calculator() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use Calculator Data Hook for reference data
  const {
    loading: dataLoading,
    services,
    servicesFullData,
    processors,
    paymentTypes,
    readerTypes,
    readerTypesWithCosts,
    billingFrequencies,
    accountExecutives,
    reloadData,
    getReaderCost
  } = useCalculatorData();

  // Helper: returns true if the given reader type has a non-zero monthly (or per-unit) cost
  // Used to SHOW the Reader Cost Recovery Tool (informational for all cost-bearing readers)
  const hasReaderMonthlyCost = (readerTypeName) => {
    if (!readerTypeName) return false;
    const rt = readerTypesWithCosts.find(r => r.name === readerTypeName);
    return rt ? (rt.monthly_cost > 0 || rt.cost_per_unit > 0) : false;
  };

  // Helper: returns true ONLY for Dynamag — the one reader type that REQUIRES cost recovery
  // before calculating. All other readers (A35, A920, etc.) can calculate without applying recovery.
  const requiresReaderCostRecovery = (readerTypeName) => {
    return readerTypeName === 'Dynamag';
  };
  
  // Use Proposal Management Hook
  const {
    selectedProposalId,
    proposalScenarios,
    setSelectedProposalId,
    setProposalScenarios,
    loadProposalScenarios,
    saveToProposal,
    deleteScenario,
    handleProposalSelect
  } = useProposalManagement();
  
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState([1, 2, 3, 4, 5]); // All sections open by default
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [result, setResult] = useState(null);
  const [profitabilityResult, setProfitabilityResult] = useState(null);
  const [profitabilityTables, setProfitabilityTables] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showServiceManager, setShowServiceManager] = useState(false);
  const [user, setUser] = useState(null);

  // Block 1: Scenario Information
  const [scenarioInfo, setScenarioInfo] = useState({
    service: '',
    billingFrequency: 'Annually',
    scenarioName: 'Base Scenario',
    asOfDate: new Date().toISOString().split('T')[0],
    readersQty: 0,
    readerType: 'N/A',
    partnership: 'Yes',
    partnershipCommissionRate: 25, // NEW: Partnership commission %, defaults to 25%
    cardProcessor: '',  // NEW: Card Processor  
    achProcessor: '',   // NEW: ACH Processor
    municipality: '',
    accountExecutive: '',
    state: '',
    selectedStateFull: '',
    accountCode: '',           // NEW: Account Code (FIPS-based identifier)
    accountCodeDetails: null   // NEW: Full account code details for reference
  });
  
  // Proposal Collection selection (selectedProposalId and proposalScenarios now managed by useProposalManagement hook)
  const [editMode, setEditMode] = useState(false);
  const [editingScenarioId, setEditingScenarioId] = useState(null);  // Track which scenario is being edited
  const editingScenarioIdRef = useRef(null); // Ref to always have current value in auto-save closures
  const [submitModal, setSubmitModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalName, setModalName] = useState('');
  const [readerCostRecoveryApplied, setReaderCostRecoveryApplied] = useState(false);
  const [originalRateBeforeRecovery, setOriginalRateBeforeRecovery] = useState(null);
  
  // Incumbent Comparison state
  const [showIncumbentBlock, setShowIncumbentBlock] = useState(false); // Collapsed by default
  const [enableIncumbentComparison, setEnableIncumbentComparison] = useState(false); // Comparison disabled by default

  // Fetch scenarios when proposal is selected (wrapped in useCallback for stability)
  // IMPORTANT: This must be defined before useScenarioManagement and usePricingCalculations hooks
  const fetchProposalScenarios = useCallback(async (proposalId) => {
    try {
      const response = await api.get(
        `/scenarios?collection_id=${proposalId}`
      );
      
      const scenarios = response.data.scenarios || [];
      console.log('📊 Fetched proposal scenarios:', scenarios.length);
      console.log('📊 First scenario sample:', scenarios[0] ? {
        has_profitability_results: !!scenarios[0].profitability_results,
        has_calculation_results: !!scenarios[0].calculation_results,
        has_totals: !!scenarios[0].profitability_results?.totals,
        total_fees: scenarios[0].profitability_results?.totals?.total_fees,
        net_revenue: scenarios[0].profitability_results?.totals?.net_revenue
      } : 'No scenarios');
      
      setProposalScenarios(scenarios);
    } catch (error) {
      console.error('Error fetching proposal scenarios:', error);
    }
  }, []);

  // Block 2: Volume Assumptions
  const [volumeAssumptions, setVolumeAssumptions] = useState({
    totalPayments: 9295,
    totalTransactions: 0,
    achPercent: 14.50,
    ccPercent: 62.00,
    amexPercent: 8.50,
    debitPercent: 15.00
  });

  // Block 3: Incumbent Pricing
  const [incumbentPricing, setIncumbentPricing] = useState({
    tier1MaxAmount: 30,
    tier1MinFee: 0.75,
    tier2MaxAmount: 48,
    tier2MinFee: 1.00,
    tier3MaxAmount: 75,
    tier3MinFee: 1.00,
    paymentRates: {
      ACH: { takeRate: 0.00, minFee: 1.35, flatFee: 0.00 },
      CC: { takeRate: 2.08, minFee: 1.50, flatFee: 0.00 },
      AMEX: { takeRate: 2.99, minFee: 1.50, flatFee: 0.00 },
      DEBIT: { takeRate: 1.50, minFee: 1.50, flatFee: 0.00 }
    }
  });

  // Block 4: Proposed Pricing
  const [proposedPricing, setProposedPricing] = useState({
    tier1MaxAmount: 0,
    tier1MinFee: 1.50,
    tier2MaxAmount: 0,
    tier2MinFee: 1.50,
    tier3MaxAmount: 0,
    tier3MinFee: 1.50,
    paymentRates: {
      ACH: { takeRate: 2.15, minFee: 1.35, merchantFee: 0.20 },
      CC: { takeRate: 2.15, minFee: 1.50, merchantFee: 1.33 },
      AMEX: { takeRate: 2.15, minFee: 1.50, merchantFee: 1.33 },
      DEBIT: { takeRate: 2.15, minFee: 1.50, merchantFee: 1.33 }
    },
    achFlatFeeAddOn: 0.50
  });

  // Block 5: Transaction Distribution
  const [transactionDistribution, setTransactionDistribution] = useState([
    { collectionAmount: '≤ $30', low: 0, mid: 15.00, high: 30.00, currentPercent: 5.7 },
    { collectionAmount: '31-48', low: 31.00, mid: 39.00, high: 48.00, currentPercent: 7.6 },
    { collectionAmount: '49-75', low: 49.00, mid: 62.00, high: 75.00, currentPercent: 16.9 },
    { collectionAmount: '76-100', low: 76.00, mid: 88.00, high: 100.00, currentPercent: 14.9 },
    { collectionAmount: '101-200', low: 101.00, mid: 150.00, high: 200.00, currentPercent: 32.2 },
    { collectionAmount: '201-500', low: 201.00, mid: 350.00, high: 500.00, currentPercent: 19.1 },
    { collectionAmount: '501-1,000', low: 501.00, mid: 750.00, high: 1000.00, currentPercent: 2.8 },
    { collectionAmount: '1,001-2,500', low: 1001.00, mid: 1750.00, high: 2500.00, currentPercent: 0.8 },
    { collectionAmount: '2,501-20,000', low: 2501.00, mid: 11250.00, high: 20000.00, currentPercent: 0.0 }
  ]);
  // Use Scenario Management Hook (handles edit, delete, duplicate, transfer reader costs, bulk operations)
  const {
    scenarioToDelete,
    readerCostTransfer,
    selectedScenarios,
    loading: scenarioLoading,
    setScenarioToDelete,
    setReaderCostTransfer,
    setSelectedScenarios,
    handleEditScenario,
    loadScenarioById,
    handleRemoveFromProposal,
    confirmRemoveScenario,
    handleTransferReaderCosts,
    handleDeleteWithoutTransfer,
    handleBulkDelete,
    handleSelectScenario,
    handleSelectAll,
    handleDuplicateScenario
  } = useScenarioManagement({
    selectedProposalId,
    proposalScenarios,
    setProposalScenarios,
    fetchProposalScenarios,
    setScenarioInfo,
    setVolumeAssumptions,
    setIncumbentPricing,
    setProposedPricing,
    setTransactionDistribution,
    setProfitabilityResult,
    setSelectedProposalId,
    setEditMode,
    setEditingScenarioId,
    setReaderCostRecoveryApplied,  // Pass recovery state setters
    setOriginalRateBeforeRecovery
  });
  // Margin Guide Sidebar state
  const [marginGuideSidebarOpen, setMarginGuideSidebarOpen] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: null, blob: null, filename: '' });

  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: null, blob: null, filename: '' });
  };

  // Auto-Save & Quality Enhancement State
  const [showValidation, setShowValidation] = useState(true);
  const [showSuggestions] = useState(true);

  // Use Pricing Calculations Hook (handles calculations, merchant fees, processor/service changes)
  const {
    calculating,
    loadingDistribution,
    totalPaymentsAutoCalculated,
    merchantFeesAutoCalculated,
    originalMerchantFees,
    achFeeType,
    usingStateOverride,
    stateOverrideInfo,
    setMerchantFeesAutoCalculated,
    setUsingStateOverride,
    setStateOverrideInfo,
    calculateTotalPayments,
    handleTotalPaymentsChange,
    resetTotalPaymentsToAuto,
    fetchMerchantFeesForService,
    handleProcessorChange,
    handleServiceChange,
    handleStateChange,
    handleResetToDefaultDistribution,
    handleCalculate,
    calculateVolumeTotal,
    calculateDistributionTotal
  } = usePricingCalculations({
    scenarioInfo,
    setScenarioInfo,
    volumeAssumptions,
    setVolumeAssumptions,
    proposedPricing,
    setProposedPricing,
    transactionDistribution,
    setTransactionDistribution,
    setResult,
    setProfitabilityResult,
    readerTypesWithCosts,
    fetchProposalScenarios,
    selectedProposalId,
    enableIncumbentComparison,
    incumbentPricing
  });
  const autoSaveData = useAutoSave('gw-calculator-draft', {
    scenarioInfo,
    volumeAssumptions,
    proposedPricing,
    transactionDistribution
  });

  // Update a single row in the transaction distribution
  // When 'low' or 'high' change, auto-recompute 'mid' and 'collectionAmount' label
  const updateDistributionRow = (index, field, value) => {
    setTransactionDistribution(prev => {
      const updated = [...prev];
      const row = { ...updated[index], [field]: value };

      if (field === 'low' || field === 'high') {
        const low = parseFloat(field === 'low' ? value : row.low) || 0;
        const high = parseFloat(field === 'high' ? value : row.high) || 0;
        row.mid = (low + high) / 2;

        // Rebuild the human-readable collectionAmount label
        const fmt = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
        if (index === 0) {
          row.collectionAmount = high > 0 ? `≤ $${fmt(high)}` : row.collectionAmount;
        } else {
          row.collectionAmount = low > 0 || high > 0 ? `${fmt(low)}-${fmt(high)}` : row.collectionAmount;
        }
      }

      updated[index] = row;
      return updated;
    });
  };

  // Handle applying reader cost recovery suggestion
  const handleApplyReaderCostSuggestion = (suggestedRates, rateIncrease) => {
    // suggestedRates should be an object with payment types and their suggested take rates
    // Example: { CC: 2.5, AMEX: 3.0, DEBIT: 2.3 }
    if (!suggestedRates || typeof suggestedRates !== 'object') {
      console.warn('Invalid suggested rates:', suggestedRates);
      return;
    }

    // Store original rate before applying recovery (for readers with monthly costs)
    if (hasReaderMonthlyCost(scenarioInfo.readerType) && 
        scenarioInfo.readersQty > 0 && 
        !originalRateBeforeRecovery) {
      setOriginalRateBeforeRecovery({
        CC: proposedPricing.paymentRates.CC?.takeRate || 0,
        AMEX: proposedPricing.paymentRates.AMEX?.takeRate || 0,
        DEBIT: proposedPricing.paymentRates.DEBIT?.takeRate || 0,
        rateIncrease: rateIncrease
      });
    }

    setProposedPricing(prev => {
      const updatedPaymentRates = { ...prev.paymentRates };
      
      // Update take rates for each payment type if suggested
      Object.entries(suggestedRates).forEach(([paymentType, suggestedRate]) => {
        if (updatedPaymentRates[paymentType]) {
          updatedPaymentRates[paymentType] = {
            ...updatedPaymentRates[paymentType],
            takeRate: suggestedRate
          };
        }
      });

      return {
        ...prev,
        paymentRates: updatedPaymentRates
      };
    });

    // Mark that reader cost recovery has been applied
    if (hasReaderMonthlyCost(scenarioInfo.readerType) && 
        scenarioInfo.readersQty > 0) {
      setReaderCostRecoveryApplied(true);
      
      // Auto-trigger calculation with new rates after a brief delay
      setTimeout(() => {
        handleCalculate();
        toast.success('✅ Reader cost recovery applied to this service');
      }, 500);
    }
  };
  
  // Handle applying reader cost recovery to ALL services in the proposal
  const handleApplyToAllServices = async (rateIncrease) => {
    if (!selectedProposalId || !proposalScenarios || proposalScenarios.length === 0) {
      toast.error('No proposal or services found');
      return;
    }
    
    try {
      setLoading(true);
      
      // Find all services with readers that have monthly costs that don't have recovery applied yet
      const servicesToUpdate = proposalScenarios.filter(scenario => {
        const scenInfo = scenario.scenario_info || {};
        const hasHighCostReaders = hasReaderMonthlyCost(scenInfo.readerType) && scenInfo.readersQty > 0;
        const recoveryApplied = scenario.reader_cost_recovery?.applied || false;
        return hasHighCostReaders && !recoveryApplied;
      });
      
      if (servicesToUpdate.length === 0) {
        toast.info('All services already have reader cost recovery applied');
        return;
      }
      
      console.log(`Applying ${rateIncrease}% rate increase to ${servicesToUpdate.length} services`);
      
      // Update each service
      const updatePromises = servicesToUpdate.map(async (scenario) => {
        const currentRates = scenario.proposed_pricing?.paymentRates || {};
        const originalRates = {
          CC: currentRates.CC?.takeRate || 0,
          AMEX: currentRates.AMEX?.takeRate || 0,
          DEBIT: currentRates.DEBIT?.takeRate || 0
        };
        
        const adjustedRates = {
          CC: originalRates.CC + rateIncrease,
          AMEX: originalRates.AMEX + rateIncrease,
          DEBIT: originalRates.DEBIT + rateIncrease
        };
        
        // Update proposed pricing with new rates
        const updatedProposedPricing = {
          ...scenario.proposed_pricing,
          paymentRates: {
            ...scenario.proposed_pricing.paymentRates,
            CC: { ...currentRates.CC, takeRate: adjustedRates.CC },
            AMEX: { ...currentRates.AMEX, takeRate: adjustedRates.AMEX },
            DEBIT: { ...currentRates.DEBIT, takeRate: adjustedRates.DEBIT }
          }
        };
        
        // Add reader cost recovery metadata
        const readerCostRecovery = {
          applied: true,
          original_rates: originalRates,
          adjusted_rates: adjustedRates,
          reader_type: scenario.scenario_info.readerType,
          reader_quantity: scenario.scenario_info.readersQty
        };
        
        // Update the scenario via API
        return api.put(`/scenarios/${scenario.id}`, {
          proposed_pricing: updatedProposedPricing,
          reader_cost_recovery: readerCostRecovery
        });
      });
      
      await Promise.all(updatePromises);
      
      toast.success(`✅ Reader cost recovery applied to ${servicesToUpdate.length} service(s)!`);
      
      // Reload proposal scenarios to reflect changes
      await fetchProposalScenarios(selectedProposalId);
      
      // Also mark current service as applied if it's one of them
      const currentServiceNeedsUpdate = servicesToUpdate.some(s => s.scenario_info?.service === scenarioInfo.service);
      if (currentServiceNeedsUpdate) {
        setReaderCostRecoveryApplied(true);
        
        // Apply to current service too
        const currentRates = {
          CC: proposedPricing.paymentRates.CC?.takeRate || 0,
          AMEX: proposedPricing.paymentRates.AMEX?.takeRate || 0,
          DEBIT: proposedPricing.paymentRates.DEBIT?.takeRate || 0
        };
        
        if (!originalRateBeforeRecovery) {
          setOriginalRateBeforeRecovery({
            ...currentRates,
            rateIncrease: rateIncrease
          });
        }
        
        setProposedPricing(prev => ({
          ...prev,
          paymentRates: {
            ...prev.paymentRates,
            CC: { ...prev.paymentRates.CC, takeRate: currentRates.CC + rateIncrease },
            AMEX: { ...prev.paymentRates.AMEX, takeRate: currentRates.AMEX + rateIncrease },
            DEBIT: { ...prev.paymentRates.DEBIT, takeRate: currentRates.DEBIT + rateIncrease }
          }
        }));
        
        // Auto-calculate
        setTimeout(() => handleCalculate(), 500);
      }
      
    } catch (error) {
      console.error('Error applying to all services:', error);
      toast.error('Failed to apply to all services');
    } finally {
      setLoading(false);
    }
  };

  // Keep editingScenarioIdRef in sync for use in auto-save closures
  useEffect(() => {
    editingScenarioIdRef.current = editingScenarioId;
  }, [editingScenarioId]);

  // Sync auto-save data with state (only on significant changes)
  useEffect(() => {
    autoSaveData.setData({
      scenarioInfo,
      volumeAssumptions,
      proposedPricing,
      transactionDistribution
    });
  }, [scenarioInfo, volumeAssumptions, proposedPricing, transactionDistribution]);

  useEffect(() => {
    loadUser();
  }, []);

  // Check URL for proposal or scenario parameter (edit mode)
  // This reacts to URL changes when navigating from other pages
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const proposalParam = urlParams.get('proposal');
    const scenarioParam = urlParams.get('scenario');
    
    if (proposalParam && proposalParam !== selectedProposalId) {
      console.log('[Calculator] Setting proposal from URL:', proposalParam);
      setSelectedProposalId(proposalParam);
      setEditMode(true);
    } else if (scenarioParam) {
      console.log('[Calculator] Loading scenario from URL:', scenarioParam);
      // Set flag to prevent auto-save when loading existing scenario
      setIsLoadingScenario(true);
      // Load specific scenario for editing
      loadScenarioById(scenarioParam).finally(() => {
        // Clear flag after scenario is loaded
        setTimeout(() => setIsLoadingScenario(false), 1000); // Small delay to ensure state settles
      });
    }
  }, [location.search, selectedProposalId, loadScenarioById]); // React to URL changes

  // Effect to fetch scenarios when proposal is selected
  useEffect(() => {
    if (selectedProposalId) {
      fetchProposalScenarios(selectedProposalId);
      setEditMode(true); // Auto-enable edit mode so checkboxes/actions are available
    } else {
      setProposalScenarios([]);
      setEditMode(false);
      setSelectedScenarios(new Set());
    }
  }, [selectedProposalId, fetchProposalScenarios]);

  // Auto-save calculation to proposal when profitability result changes
  // Uses editingScenarioIdRef so edits UPDATE existing scenario instead of creating duplicates
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);
  
  useEffect(() => {
    const autoSaveCalculation = async () => {
      // Only auto-save if:
      // 1. A proposal is selected
      // 2. We have a successful profitability result
      // 3. NOT currently loading an existing scenario (prevent duplicate saves on load)
      if (selectedProposalId && profitabilityResult && profitabilityResult.success && !isLoadingScenario) {
        console.log('[Calculator] Auto-saving calculation to proposal:', selectedProposalId, '| editing:', editingScenarioIdRef.current);
        
        // Build complete calculation data — include scenario_id if editing so we UPDATE not INSERT
        const calculationData = {
          scenario_id: editingScenarioIdRef.current || null,
          service_name: scenarioInfo.service,
          scenario_name: scenarioInfo.scenarioName || `${scenarioInfo.service} - ${new Date().toLocaleDateString()}`,
          scenario_info: scenarioInfo,
          volume_assumptions: volumeAssumptions,
          incumbent_pricing: enableIncumbentComparison ? incumbentPricing : null,
          proposed_pricing: proposedPricing,
          transaction_distribution: transactionDistribution,
          calculation_results: profitabilityResult,
          profitability_results: profitabilityResult
        };

        // Auto-save to proposal
        await saveToProposal(calculationData, selectedProposalId);
      }
    };

    autoSaveCalculation();
  }, [profitabilityResult]); // Only trigger when profitabilityResult changes



  // Submit selected calculations for approval
  const handleSubmitSelected = async () => {
    if (selectedScenarios.size === 0) {
      toast.error('Please select at least one calculation to submit');
      return;
    }
    setSubmitModal(true);
  };

  const confirmSubmitSelected = async () => {
    // Check if any selected scenarios have Dynamag readers without recovery applied
    const selectedIds = Array.from(selectedScenarios);
    const selectedScenariosData = proposalScenarios.filter(s => selectedIds.includes(s.id));
    
    const scenariosNeedingRecovery = selectedScenariosData.filter(scenario => {
      const scenInfo = scenario.scenario_info || {};
      const hasHighCostReaders = requiresReaderCostRecovery(scenInfo.readerType) && scenInfo.readersQty > 0;
      const recoveryApplied = scenario.reader_cost_recovery?.applied || false;
      return hasHighCostReaders && !recoveryApplied;
    });
    
    if (scenariosNeedingRecovery.length > 0) {
      const serviceNames = scenariosNeedingRecovery.map(s => s.service_name || 'Unnamed Service').join(', ');
      toast.error(
        <div>
          <p className="font-bold mb-2">Reader Cost Recovery Required</p>
          <p className="text-sm">The following services need reader cost recovery applied:</p>
          <ul className="text-sm mt-2 list-disc ml-4">
            {scenariosNeedingRecovery.map(s => (
              <li key={s.id}>{s.service_name || 'Unnamed Service'} ({s.scenario_info?.readerType} - {s.scenario_info?.readersQty} units)</li>
            ))}
          </ul>
        </div>,
        { duration: 8000 }
      );
      return;
    }
    
    setSubmitModal(false);
    try {
      setLoading(true);
      
      await Promise.all(selectedIds.map(id => 
        api.post(`/scenarios/${id}/submit`, 
          {}
        )
      ));
      
      toast.success(`${selectedIds.length} calculation(s) submitted for approval`);
      setSelectedScenarios(new Set());
      setEditMode(false);
      await fetchProposalScenarios(selectedProposalId);
    } catch (error) {
      console.error('Error submitting calculations:', error);
      toast.error('Failed to submit calculations');
    } finally {
      setLoading(false);
    }
  };

  // Handle save calculation to proposal
  const handleSaveToProposal = async () => {
    if (!profitabilityResult) {
      toast.error('Please calculate first before saving');
      return;
    }
    
    if (!selectedProposalId) {
      toast.error('Please select a proposal first');
      return;
    }

    // Build complete calculation data for saving
    const calculationData = {
      scenario_id: editingScenarioId,  // Include the scenario ID if editing
      service_name: scenarioInfo.service,
      scenario_name: scenarioInfo.scenarioName || `${scenarioInfo.service} - ${new Date().toLocaleDateString()}`,
      scenario_info: scenarioInfo,
      volume_assumptions: volumeAssumptions,
      incumbent_pricing: incumbentPricing,
      proposed_pricing: proposedPricing,
      transaction_distribution: transactionDistribution,
      profitability_results: profitabilityResult,
      // Add reader cost recovery metadata if applied
      reader_cost_recovery: hasReaderMonthlyCost(scenarioInfo.readerType) && 
                            scenarioInfo.readersQty > 0 && 
                            readerCostRecoveryApplied ? {
        applied: true,
        original_rates: originalRateBeforeRecovery,
        adjusted_rates: {
          CC: proposedPricing.paymentRates.CC?.takeRate || 0,
          AMEX: proposedPricing.paymentRates.AMEX?.takeRate || 0,
          DEBIT: proposedPricing.paymentRates.DEBIT?.takeRate || 0
        },
        reader_type: scenarioInfo.readerType,
        reader_quantity: scenarioInfo.readersQty
      } : null
    };

    const success = await saveToProposal(calculationData, selectedProposalId);
    
    // If save was successful and we were editing, clear the editing state
    if (success && editingScenarioId) {
      setEditingScenarioId(null);
      toast.info('💡 Ready for a new calculation');
    }
  };

  // Handle submit current proposal for approval (Quick Submit from Calculator)
  const handleSubmitForApproval = async () => {
    if (!selectedProposalId) {
      toast.error('Please select a proposal first');
      return;
    }
    
    // Check if there are any calculations in this proposal
    if (!proposalScenarios || proposalScenarios.length === 0) {
      toast.error('Please add at least one calculation to this proposal before submitting');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post(`/collections/${selectedProposalId}/submit`);
      toast.success(response.data.message || 'Proposal submitted for approval successfully!');
      
      // Reload proposal data to reflect updated status
      await fetchProposalScenarios(selectedProposalId);
      
      // Optionally navigate to the proposal view page
      // navigate(`/proposals/${selectedProposalId}`);
    } catch (error) {
      console.error('Error submitting proposal:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit proposal for approval';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  // Add a new service to the current proposal (reset form, keep proposal selected)
  const handleAddNewService = () => {
    // Clear editing state
    setEditingScenarioId(null);
    
    // Reset form fields for a fresh calculation
    setScenarioInfo(prev => ({
      ...prev,
      service: '',
      scenarioName: 'Base Scenario',
      readersQty: 0,
      readerType: 'N/A',
    }));
    setResult(null);
    setProfitabilityResult(null);
    setSelectedScenarios(new Set());
    
    // Scroll to the top of the calculator form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info('Ready to add a new service to this proposal');
  };

  // Export selected scenarios to Excel
  const handleExportExcel = async () => {
    if (selectedScenarios.size === 0) {
      toast.error('Please select at least one calculation to export');
      return;
    }
    try {
      setLoading(true);
      const selectedIds = Array.from(selectedScenarios);
      const { downloadViaPost } = await import('@/utils/downloadHelper');
      await downloadViaPost(
        '/scenarios/export/excel',
        { scenario_ids: selectedIds, include_details: true },
        `scenarios_${Date.now()}.xlsx`
      );
      toast.success(`Exported ${selectedIds.length} calculation(s) to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    } finally {
      setLoading(false);
    }
  };

  // Export selected scenarios to PDF
  const handleExportPDF = async () => {
    if (selectedScenarios.size === 0) {
      toast.error('Please select at least one calculation to export');
      return;
    }
    try {
      setLoading(true);
      const selectedIds = Array.from(selectedScenarios);
      const res = await api.post('/scenarios/export/pdf', { scenario_ids: selectedIds }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const filename = `scenarios_${Date.now()}.pdf`;
      setPdfViewer({ show: true, url: blobUrl, blob, filename });
      toast.success(`Exported ${selectedIds.length} calculation(s) to PDF`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF');
    } finally {
      setLoading(false);
    }
  };

  // Get approval badge component
  // Auto-calculate Total Payments when it's in auto mode
  useEffect(() => {
    if (totalPaymentsAutoCalculated) {
      calculateTotalPayments();
    }
  }, [
    volumeAssumptions.totalTransactions, 
    JSON.stringify(transactionDistribution),  // Deep comparison for array changes
    totalPaymentsAutoCalculated
  ]);


  const loadUser = () => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${((value || 0) * 100).toFixed(2)}%`;
  };

  // Trigger reload flags when leaving Calculator (to refresh other pages on navigation)
  useEffect(() => {
    return () => {
      // Cleanup: Set flags for other pages to reload when user navigates away
      console.log('[Calculator] Setting reload flags for Proposals and History on unmount');
      localStorage.setItem('reload_proposals', 'true');
      localStorage.setItem('reload_scenarios', 'true');
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Edit Mode Banner */}
      {editMode && (
        <div className="bg-slate-900 text-white py-2.5 px-6 shadow-sm">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm">
              <svg className="w-4 h-4 text-sky-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              <span className="font-medium">Edit Mode</span>
              <span className="text-slate-500">|</span>
              <span className="text-slate-400">Click calculations to edit, select to submit for approval</span>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="text-white hover:text-indigo-100 underline text-sm"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <CalculatorHeader
        selectedProposalId={selectedProposalId}
        profitabilityResult={profitabilityResult}
        calculating={calculating}
        onSaveToProposal={handleSaveToProposal}
        onReloadData={reloadData}
        onCalculate={handleCalculate}
      />

      {/* Editing Indicator Banner */}
      {editingScenarioId && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-lg">✏️</span>
              </div>
              <div>
                <h4 className="text-blue-900 font-bold text-sm">Editing Existing Calculation</h4>
                <p className="text-blue-700 text-xs">
                  Make your changes and click <strong>"Calculate"</strong> to update. 
                  Changes will save to the same calculation (no duplicate will be created).
                </p>
              </div>
            </div>
            <button
              onClick={handleAddNewService}
              className="px-4 py-2 bg-white border-2 border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-semibold text-sm"
            >
              Cancel Edit
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Progress Tracker */}
        <CalculatorProgressTracker
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={(step) => {
            setCurrentStep(step);
            // Scroll to section
            const sectionId = `block-${step}`;
            document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />

        {/* Quick Start Templates */}
        {!profitabilityResult && (
          <QuickStartTemplates
            onSelectTemplate={(templateData) => {
              setVolumeAssumptions(templateData.volumeAssumptions);
              setIncumbentPricing(templateData.incumbentPricing);
              setProposedPricing(templateData.proposedPricing);
              toast.success('Template applied! Fill in Client Info to continue.');
              setCurrentStep(1);
            }}
          />
        )}

        {/* Smart Suggestions */}
        {!profitabilityResult && showSuggestions && (
          <div className="mb-6">
            <SmartSuggestions
              serviceId={scenarioInfo.serviceId}
              volumeAssumptions={volumeAssumptions}
              proposedPricing={proposedPricing}
              visible={showSuggestions}
            />
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Input Blocks */}
          <div className="xl:col-span-2 space-y-6">
            {/* Proposal Selector */}
            <ProposalSelector
              selectedProposalId={selectedProposalId}
              onProposalChange={setSelectedProposalId}
              className="card p-4"
            />
            
            {/* BLOCK 1: SCENARIO INFORMATION */}
            <div id="block-1" className="space-y-3">
              <CalculatorHelp section="clientInfo" collapsed={currentStep !== 1} />
              <ClientInfoBlock
                scenarioInfo={scenarioInfo}
                setScenarioInfo={setScenarioInfo}
                services={services}
                handleServiceChange={handleServiceChange}
                billingFrequencies={billingFrequencies}
                accountExecutives={accountExecutives}
                readerTypes={readerTypes}
                readerTypesWithCosts={readerTypesWithCosts}
                processors={processors}
                user={user}
                setShowServiceManager={setShowServiceManager}
                onProcessorChange={handleProcessorChange}
              />
              
              {/* Target Margins Display - Shows when service is selected */}
              {scenarioInfo.service && (
                <TargetMarginsDisplay 
                  selectedService={scenarioInfo.service}
                  servicesFullData={servicesFullData}
                />
              )}
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                <p className="text-xs text-blue-900">
                  <strong>💡 Quick Tip:</strong> Select your service, state, processor, and account executive. Reader quantities and billing frequency are optional but affect calculations.
                </p>
              </div>
            </div>
            
            {/* READER COST RECOVERY CALCULATOR */}
            {scenarioInfo.readersQty > 0 && scenarioInfo.readerType && scenarioInfo.readerType !== 'N/A' && (
              <div className="mb-6">
                <ReaderCostRecoveryTool
                  scenarioInfo={scenarioInfo}
                  volumeAssumptions={volumeAssumptions}
                  proposedPricing={proposedPricing}
                  readerTypesWithCosts={readerTypesWithCosts}
                  proposalScenarios={proposalScenarios}
                  onApplySuggestion={handleApplyReaderCostSuggestion}
                  readerCostRecoveryApplied={readerCostRecoveryApplied}
                  onApplyToAllServices={handleApplyToAllServices}
                />
              </div>
            )}

            {/* BLOCK 2: VOLUME ASSUMPTIONS */}
            <div id="block-2" className="space-y-3">
              <CalculatorHelp section="volume" collapsed={currentStep !== 2} />
              <VolumeAssumptionsBlock
                volumeAssumptions={volumeAssumptions}
                setVolumeAssumptions={setVolumeAssumptions}
                totalPaymentsAutoCalculated={totalPaymentsAutoCalculated}
                handleTotalPaymentsChange={handleTotalPaymentsChange}
                resetTotalPaymentsToAuto={resetTotalPaymentsToAuto}
                calculateVolumeTotal={calculateVolumeTotal}
              />
            </div>

            {/* BLOCK 3: INCUMBENT PRICING (OPTIONAL COMPARISON) */}
            <div id="block-3" className="space-y-3">
              {showIncumbentBlock && <CalculatorHelp section="incumbentPricing" collapsed={currentStep !== 3} />}
            <div className="card-modern p-0 overflow-hidden">
              {/* Red Banner Header - Always Visible */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 relative">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-white">BLOCK 3: INCUMBENT PRICING (Optional Comparison)</h2>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      showIncumbentBlock 
                        ? 'bg-white/20 text-white border border-white/30' 
                        : 'bg-white/10 text-white/80 border border-white/20'
                    }`}>
                      {showIncumbentBlock ? 'Expanded' : 'Collapsed'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {showIncumbentBlock && (
                      <label 
                        className="flex items-center gap-2 text-sm cursor-pointer bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={enableIncumbentComparison}
                          onChange={(e) => setEnableIncumbentComparison(e.target.checked)}
                          className="w-4 h-4 rounded focus:ring-white"
                        />
                        <span className="text-white font-medium">Enable Comparison</span>
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowIncumbentBlock(!showIncumbentBlock)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      aria-label={showIncumbentBlock ? "Collapse block" : "Expand block"}
                    >
                      {showIncumbentBlock ? (
                        <ChevronDown className="w-5 h-5 text-white" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-white" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Collapsible Content */}
              {showIncumbentBlock && (
                <div className="p-6 animate-in slide-in-from-top-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-900 mb-2">
                      <strong>💡 How to use:</strong> Enter your incumbent provider's pricing structure below to see a side-by-side comparison.
                    </p>
                    <p className="text-xs text-blue-700">
                      Check "Enable Comparison" above to include this in your calculations.
                    </p>
                  </div>
                  <IncumbentPricingBlock
                    incumbentPricing={incumbentPricing}
                    setIncumbentPricing={setIncumbentPricing}
                    proposedPricing={proposedPricing}
                    achFeeType={achFeeType}
                  />
                </div>
              )}
            </div>
            </div>

            {/* BLOCK 4: PROPOSED PRICING */}
            <div id="block-4" className="space-y-3">
              <CalculatorHelp section="proposedPricing" collapsed={currentStep !== 4} />
              <ProposedPricingBlock
                proposedPricing={proposedPricing}
                setProposedPricing={setProposedPricing}
                merchantFeesAutoCalculated={merchantFeesAutoCalculated}
                setMerchantFeesAutoCalculated={setMerchantFeesAutoCalculated}
                achFeeType={achFeeType}
                userRole={user?.role}
                originalMerchantFees={originalMerchantFees}
                readerCostRecoveryApplied={readerCostRecoveryApplied}
                originalRateBeforeRecovery={originalRateBeforeRecovery}
              />
              <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded-r-lg">
                <p className="text-xs text-green-900">
                  <strong>💡 Quick Tip:</strong> Set take rates (%) for each payment type. Minimum fees are optional. Merchant fees can auto-populate from settings or be customized. Mark fields with <span className="text-red-600">*</span> as required.
                </p>
              </div>
            </div>

            {/* BLOCK 5: TRANSACTION DISTRIBUTION */}
            <div id="block-5" className="space-y-3">
              <CalculatorHelp section="distribution" collapsed={currentStep !== 5} />
              <DistributionBlock
                transactionDistribution={transactionDistribution}
                updateDistributionRow={updateDistributionRow}
                calculateDistributionTotal={calculateDistributionTotal}
                usingStateOverride={usingStateOverride}
                stateOverrideInfo={stateOverrideInfo}
                onResetToDefault={handleResetToDefaultDistribution}
                isLoadingDistribution={loadingDistribution}
              />
              <div className="bg-purple-50 border-l-4 border-purple-400 p-3 rounded-r-lg">
                <p className="text-xs text-purple-900">
                  <strong>💡 Quick Tip:</strong> Distribute transactions across payment types and amount tiers. Each tier shows Low/Mid/High amounts and a percentage. Total must equal 100%. State-specific distributions auto-load when available.
                </p>
              </div>
            </div>

            {/* Calculate Button - Big and Prominent */}
            <div className="card p-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
              <div className="flex flex-col items-center space-y-4">
                {/* Reader Cost Recovery Warning — Dynamag only requires this before calculating */}
                {requiresReaderCostRecovery(scenarioInfo.readerType) && scenarioInfo.readersQty > 0 && !readerCostRecoveryApplied && (
                  <div className="w-full bg-red-50 border-2 border-red-400 rounded-lg p-4 mb-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-900 font-bold text-sm mb-1">
                          ⚠️ Reader Cost Recovery Required
                        </p>
                        <p className="text-red-800 text-xs">
                          You must apply the reader cost recovery suggestion before calculating. 
                          Scroll up to the <strong>"💡 Reader Cost Recovery Calculator"</strong> and click <strong>"Apply Suggestion"</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-slate-700 text-center max-w-2xl">
                  Ready to calculate? Click below to run the pricing analysis and view detailed results.
                </p>
                <button
                  onClick={handleCalculate}
                  disabled={calculating || (requiresReaderCostRecovery(scenarioInfo.readerType) && scenarioInfo.readersQty > 0 && !readerCostRecoveryApplied)}
                  className="btn-primary text-lg px-12 py-4 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:transform-none disabled:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CalcIcon className="w-6 h-6 mr-3" />
                  {calculating ? 'Calculating...' : requiresReaderCostRecovery(scenarioInfo.readerType) && scenarioInfo.readersQty > 0 && !readerCostRecoveryApplied ? 'Apply Reader Cost Recovery First' : 'Calculate Pricing'}
                </button>
                <p className="text-sm text-slate-500">
                  {result ? '✓ Calculation complete. Click again to recalculate with updated values.' : 'Complete all required fields above before calculating.'}
                </p>
              </div>
            </div>
          </div>

          {/* Results Section - Full Width Below Inputs */}
          {profitabilityResult && (profitabilityResult.proposed || profitabilityResult.success || profitabilityResult.totals) && (
            <div id="calculation-results" className="xl:col-span-3 space-y-6 mt-8">
              <GWProfitabilityResults profitabilityData={profitabilityResult} userRole={user?.role} />
              
              {/* Detailed Profitability Tables - CC/ACH Breakdown */}
              {profitabilityResult && profitabilityResult.payment_types && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                  <ProfitabilityTablesDetailed 
                    profitabilityResult={profitabilityResult} 
                    loading={calculating}
                  />
                </div>
              )}
              
              {/* Approval Flags */}
              <ApprovalFlags 
                profitabilityData={profitabilityResult.proposed || profitabilityResult} 
                scenarioInfo={scenarioInfo}
              />
              
              {/* Action Buttons */}
              <CalculatorActions
                selectedProposalId={selectedProposalId}
                onSubmitForApproval={handleSubmitForApproval}
                calculationsCount={proposalScenarios.length}
              />
            </div>
          )}
          
          {profitabilityResult && profitabilityResult.success === false && profitabilityResult.error && (
            <div id="calculation-results" className="xl:col-span-3 space-y-6 mt-8">
              <div className="card p-6 bg-red-50 border-l-4 border-l-red-500">
                <h3 className="text-lg font-bold text-red-900 mb-2">Calculation Error</h3>
                <p className="text-red-700">{profitabilityResult.error || 'An error occurred during calculation'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Proposal Scenarios List - Show when proposal is selected */}
        {selectedProposalId && proposalScenarios.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 pb-8">
            {/* Aggregated Summary Section */}
            <ProposalSummary proposalScenarios={proposalScenarios} />
            
            {/* Individual Calculations List */}
            <ProposalScenariosList
              proposalScenarios={proposalScenarios}
              editMode={editMode}
              selectedScenarios={selectedScenarios}
              onSelectAll={handleSelectAll}
              onSelectScenario={handleSelectScenario}
              onEditScenario={handleEditScenario}
              onDuplicateScenario={handleDuplicateScenario}
              onRemoveFromProposal={handleRemoveFromProposal}
              onSubmitSelected={handleSubmitSelected}
              onAddNewService={handleAddNewService}
              onBulkDelete={handleBulkDelete}
            />
          </div>
        )}
      </div>

      {/* Service Manager Modal */}
      <ServiceManagerEnhanced 
        isOpen={showServiceManager}
        onClose={() => setShowServiceManager(false)}
        onServicesUpdated={reloadData}
      />

      {/* Smart Margin Guide Sidebar */}
      <MarginGuideSidebar
        services={servicesFullData}
        processors={processors}
        isOpen={marginGuideSidebarOpen}
        onToggle={() => setMarginGuideSidebarOpen(!marginGuideSidebarOpen)}
        selectedService={servicesFullData.find(s => s.name === scenarioInfo.service)?.id || null}
      />
      
      {/* Delete Scenario Confirmation Modal */}
      {scenarioToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Confirm Deletion</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to remove "<strong>{scenarioToDelete.name}</strong>" from this proposal? 
              This will permanently delete this calculation.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setScenarioToDelete(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRemoveScenario}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reader Cost Transfer Modal */}
      {readerCostTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900">Transfer Reader Costs</h3>
                <p className="text-sm text-slate-500 mt-1">
                  "{readerCostTransfer.scenarioName}" has reader costs
                </p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-900">Reader Costs to Transfer:</span>
                <span className="text-lg font-bold text-amber-900">
                  ${readerCostTransfer.readerCosts.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {readerCostTransfer.readerCostDetails && (
                <p className="text-xs text-amber-700 mt-2">
                  {readerCostTransfer.readerCostDetails.quantity || 0} reader(s) @ ${(readerCostTransfer.readerCostDetails.cost_per_reader || 0).toFixed(2)} each
                </p>
              )}
            </div>

            <p className="text-slate-600 mb-4 text-sm">
              Would you like to transfer these reader costs to another service in this proposal, or discard them?
            </p>

            <div className="space-y-3 mb-6 max-h-48 overflow-y-auto">
              {readerCostTransfer.remainingServices.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => handleTransferReaderCosts(service.id)}
                  disabled={loading}
                  className="w-full text-left p-3 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{service.service_name || service.name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {service.scenario_info?.state || 'N/A'} • {service.scenario_info?.processor || 'N/A'}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setReaderCostTransfer(null)}
                disabled={loading}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteWithoutTransfer}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Discard Costs & Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit for Approval Confirmation Modal */}
      {submitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div data-testid="submit-confirm-modal" className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Submit for Approval</h3>
            
            {/* Reader Cost Recovery REQUIRED — any reader type with monthly costs */}
            {hasReaderMonthlyCost(scenarioInfo.readerType) && scenarioInfo.readersQty > 0 && (
              <div className="mb-6">
                {!readerCostRecoveryApplied ? (
                  <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xl font-bold">⚠️</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-red-900 font-bold text-base mb-2">
                          🚫 SUBMISSION BLOCKED: Reader Cost Recovery Required
                        </h4>
                        <div className="bg-white border border-red-300 rounded p-3">
                          <p className="text-red-800 font-semibold text-sm mb-2">
                            This proposal uses <span className="font-bold">{scenarioInfo.readerType}</span> readers ({scenarioInfo.readersQty} units).
                          </p>
                          <p className="text-red-700 text-sm mb-3">
                            You MUST click <strong>"Apply Suggestion"</strong> in the Reader Cost Recovery Calculator before submitting.
                          </p>
                          <div className="bg-red-100 rounded p-2">
                            <p className="text-red-900 font-bold text-xs mb-1">
                              📍 REQUIRED STEPS:
                            </p>
                            <ol className="text-red-800 text-xs ml-4 list-decimal space-y-1">
                              <li>Close this modal</li>
                              <li>Find the <strong>Reader Cost Recovery Calculator</strong> (yellow box in Block 3)</li>
                              <li>Click the <strong>"Apply Suggestion"</strong> button</li>
                              <li>Click <strong>"Calculate"</strong> to see updated profitability</li>
                              <li>Return here to submit</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xl font-bold">✓</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-green-900 font-bold text-base mb-2">
                          ✅ Reader Cost Recovery Applied
                        </h4>
                        <div className="bg-white border border-green-300 rounded p-3">
                          <p className="text-green-800 font-semibold text-sm mb-2">
                            Rate adjustment applied for {scenarioInfo.readerType} reader deployment
                          </p>
                          {originalRateBeforeRecovery && (
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between items-center py-1 border-b border-green-200">
                                <span className="text-slate-600">CC Rate (Original):</span>
                                <span className="font-mono text-red-600 line-through">{originalRateBeforeRecovery.CC?.toFixed(2)}%</span>
                              </div>
                              <div className="flex justify-between items-center py-1 border-b border-green-200">
                                <span className="text-slate-600">CC Rate (Adjusted):</span>
                                <span className="font-mono font-bold text-green-700">{proposedPricing.paymentRates.CC?.takeRate?.toFixed(2)}%</span>
                              </div>
                              <p className="text-xs text-green-700 mt-2 font-semibold">
                                ⚡ Rate increased by {(proposedPricing.paymentRates.CC?.takeRate - originalRateBeforeRecovery.CC).toFixed(2)}% to recover reader costs
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <p className="text-slate-600 mb-6">
              Submit <span className="font-semibold">{selectedScenarios.size} calculation{selectedScenarios.size !== 1 ? 's' : ''}</span> for approval?
            </p>
            <div className="flex justify-end gap-3">
              <button
                data-testid="cancel-submit-btn"
                type="button"
                onClick={() => setSubmitModal(false)}
                className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                id="confirm-submit-btn"
                data-testid="confirm-submit-btn"
                type="button"
                onClick={confirmSubmitSelected}
                disabled={requiresReaderCostRecovery(scenarioInfo.readerType) && scenarioInfo.readersQty > 0 && !readerCostRecoveryApplied}
                className="px-4 py-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requiresReaderCostRecovery(scenarioInfo.readerType) && scenarioInfo.readersQty > 0 && !readerCostRecoveryApplied ? 'Apply Recovery First' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pdfViewer.show && (
        <PdfViewerModal
          url={pdfViewer.url}
          name={pdfViewer.filename.replace('.pdf', '')}
          blob={pdfViewer.blob}
          filename={pdfViewer.filename}
          onClose={closePdfViewer}
        />
      )}

      {/* Real-Time Validation */}
      <CalculatorValidation
        scenarioInfo={scenarioInfo}
        volumeAssumptions={volumeAssumptions}
        proposedPricing={proposedPricing}
        transactionDistribution={transactionDistribution}
        showValidation={showValidation}
      />

      {/* Auto-Save Indicator */}
      <AutoSaveIndicator
        saveStatus={autoSaveData.saveStatus}
        lastSaved={autoSaveData.lastSaved}
      />
    </div>
  );
}
